/**
 * Next50 scroll-reveal + count-up animations.
 * Opt-in via data-n50-reveal (legacy) or data-n50-reveal-block (tiered zones).
 * Respects prefers-reduced-motion.
 */

import { onDocumentReady, prefersReducedMotion } from '@theme/utilities';

const REVEAL_SELECTOR = '[data-n50-reveal]';
const BLOCK_SELECTOR = '[data-n50-reveal-block]';
const COUNT_SELECTOR = '[data-n50-count-up]';

const SCROLL_ROOT_MARGIN = '0px 0px -8% 0px';
const SCROLL_THRESHOLD = 0.18;
const EAGER_ROOT_MARGIN = '0px 0px -5% 0px';
const EAGER_THRESHOLD = 0.14;

/** @typedef {'default' | 'eager'} ObserverMode */

/** @type {Record<ObserverMode, IntersectionObserverInit>} */
const OBSERVER_OPTIONS = {
  default: { rootMargin: SCROLL_ROOT_MARGIN, threshold: SCROLL_THRESHOLD },
  eager: { rootMargin: EAGER_ROOT_MARGIN, threshold: EAGER_THRESHOLD },
};

/** @param {Element} el @returns {ObserverMode} */
function getObserverMode(el) {
  return el.closest('[data-n50-trigger="eager"]') ? 'eager' : 'default';
}

/** @param {Element[]} elements @param {(el: Element) => void} onReveal */
function observeWithModes(elements, onReveal) {
  /** @type {Record<ObserverMode, Element[]>} */
  const groups = { default: [], eager: [] };

  elements.forEach((el) => {
    groups[getObserverMode(el)].push(el);
  });

  /** @type {ObserverMode[]} */
  (['default', 'eager']).forEach((mode) => {
    const list = groups[mode];
    if (!list.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          onReveal(entry.target);
          observer.unobserve(entry.target);
        });
      },
      OBSERVER_OPTIONS[mode]
    );

    list.forEach((el) => observer.observe(el));
  });
}
const SAFETY_NET_MS = 2500;
const NEAR_ON_LOAD_VIEWPORT_RATIO = 0.85;

const COUNT_DURATION_DEFAULT = 2000;
const COUNT_DELAY_DEFAULT = 200;

const ZONE_DURATIONS = { atf: 400, near: 450, scroll: 520 };

const FRACTION_PATTERN = /\bin\b/i;
const TIME_PATTERN =
  /(?:\d+\s*\/\s*\d+|\d{1,2}:\d{2}|\b\d+\s*(?:am|pm|hrs?|hours?|min(?:ute)?s?|sec(?:ond)?s?)\b)/i;
const COUNTABLE_PATTERN = /^\s*\$?[\d,.]+(?:%|[×x]|\+)?\s*$/i;

/** @param {string} text */
function isCountUpEligible(text) {
  const trimmed = String(text).trim();
  if (!trimmed) return false;
  if (FRACTION_PATTERN.test(trimmed)) return false;
  if (TIME_PATTERN.test(trimmed)) return false;
  if (!COUNTABLE_PATTERN.test(trimmed)) return false;
  return parseCountTarget(trimmed) !== null;
}

/** @param {string} text */
function parseCountTarget(text) {
  const match = String(text).trim().match(/([\d,.]+)/);
  if (!match) return null;
  const num = parseFloat(match[1].replace(/,/g, ''));
  if (!Number.isFinite(num)) return null;

  const raw = match[1];
  const decimalPlaces = raw.includes('.') ? raw.split('.')[1].length : 0;
  return { num, raw, decimalPlaces };
}

/** @param {Element} el @param {string} attr */
function readCountTiming(el, attr, fallback) {
  const own = el.getAttribute(attr);
  if (own != null && own !== '') {
    const parsed = parseInt(own, 10);
    if (Number.isFinite(parsed)) return parsed;
  }
  const parent = el.closest(`[${attr}]`);
  if (parent && parent !== el) {
    const inherited = parent.getAttribute(attr);
    if (inherited != null && inherited !== '') {
      const parsed = parseInt(inherited, 10);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return fallback;
}

/** @param {HTMLElement} el */
function animateCountUp(el) {
  const original = el.textContent?.trim() || '';
  if (!isCountUpEligible(original)) {
    el.removeAttribute('data-n50-count-up');
    return;
  }

  const parsed = parseCountTarget(original);
  if (!parsed) return;

  const before = original.slice(0, original.indexOf(parsed.raw));
  const after = original.slice(original.indexOf(parsed.raw) + parsed.raw.length);
  const target = parsed.num;
  const duration = readCountTiming(el, 'data-n50-count-duration', COUNT_DURATION_DEFAULT);
  const startDelay = readCountTiming(el, 'data-n50-count-delay', COUNT_DELAY_DEFAULT);
  const { decimalPlaces } = parsed;

  /** @param {number} value */
  function formatValue(value) {
    if (decimalPlaces > 0) {
      return value.toFixed(decimalPlaces);
    }
    return Math.round(value).toLocaleString();
  }

  /** @param {number} start */
  function runCount(start) {
    /** @param {number} now */
    function tick(now) {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - (1 - t) ** 3;
      const current = target * eased;
      el.textContent = before + formatValue(current) + after;
      if (t < 1) requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  }

  if (startDelay > 0) {
    setTimeout(() => runCount(performance.now()), startDelay);
  } else {
    runCount(performance.now());
  }
}

/** @param {Element} el */
function revealElement(el) {
  if (el.classList.contains('is-visible')) return;
  const delay = parseInt(el.getAttribute('data-n50-delay') || '0', 10) || 0;
  if (delay > 0) {
    setTimeout(() => el.classList.add('is-visible'), delay);
  } else {
    el.classList.add('is-visible');
  }
}

/** @param {Element} el */
function revealBlock(el) {
  if (el.classList.contains('is-visible')) return;
  const delay = parseInt(el.getAttribute('data-n50-delay') || '0', 10) || 0;
  const apply = () => {
    el.classList.add('is-visible');
    triggerCountUpInBlock(el);
  };
  if (delay > 0) {
    setTimeout(apply, delay);
  } else {
    apply();
  }
}

/** @param {Element} block */
function triggerCountUpInBlock(block) {
  block.querySelectorAll(COUNT_SELECTOR).forEach((el) => {
    const counter = /** @type {HTMLElement} */ (el);
    if (counter.dataset.n50CountDone === 'true') return;

    const text = counter.textContent?.trim() || '';
    if (!isCountUpEligible(text)) {
      counter.removeAttribute('data-n50-count-up');
      return;
    }

    counter.dataset.n50CountDone = 'true';
    animateCountUp(counter);
  });
}

/** @param {Element} el */
function isRevealLoad(el) {
  return el.hasAttribute('data-n50-reveal-load');
}

/** @param {Element} el */
function isRevealOnLoad(el) {
  return el.hasAttribute('data-n50-reveal-on-load');
}

/** @param {Element} el */
function getBlockZone(el) {
  return el.getAttribute('data-n50-zone') || 'scroll';
}

/** On-load pre-reveal for non-ATF blocks: near zone only, upper viewport. Scroll blocks wait for observer. */
/** @param {Element} el */
function shouldRevealOnLoad(el) {
  const zone = getBlockZone(el);
  if (zone === 'scroll') return false;

  const rect = el.getBoundingClientRect();
  if (zone === 'near') {
    return rect.top < window.innerHeight * NEAR_ON_LOAD_VIEWPORT_RATIO && rect.bottom > 0;
  }

  return false;
}

/** @param {Element} el */
function syncLoadRevealDelay(el) {
  if (!isRevealLoad(el) && !isRevealOnLoad(el)) return;
  const delay = el.getAttribute('data-n50-delay');
  if (delay) el.style.setProperty('--n50-reveal-delay', `${delay}ms`);
}

/** @param {Element} el */
function blockZoneDuration(el) {
  const zone = el.getAttribute('data-n50-zone') || 'scroll';
  return ZONE_DURATIONS[/** @type {keyof typeof ZONE_DURATIONS} */ (zone)] ?? ZONE_DURATIONS.scroll;
}

function applyStaggerDelays() {
  document.querySelectorAll('[data-n50-stagger]').forEach((parent) => {
    const step = parseInt(parent.getAttribute('data-n50-stagger') || '80', 10) || 80;
    const children = parent.querySelectorAll(`${REVEAL_SELECTOR}, ${BLOCK_SELECTOR}`);
    let index = 0;
    children.forEach((el) => {
      if (!el.hasAttribute('data-n50-delay')) {
        el.setAttribute('data-n50-delay', String(index * step));
        syncLoadRevealDelay(el);
      }
      index += 1;
    });
  });

  document
    .querySelectorAll('[data-n50-reveal-load][data-n50-delay], [data-n50-reveal-on-load][data-n50-delay]')
    .forEach(syncLoadRevealDelay);
}

/** @param {NodeListOf<Element>} blocks */
function revealBlocksOnLoad(blocks) {
  blocks.forEach((el) => {
    if (isRevealLoad(el) || isRevealOnLoad(el)) return;
    if (shouldRevealOnLoad(el)) revealBlock(el);
  });
}

/** @param {NodeListOf<Element>} blocks */
function scheduleBlockSafetyNet(blocks) {
  blocks.forEach((el) => {
    setTimeout(() => {
      if (isRevealLoad(el)) {
        const style = getComputedStyle(el);
        if (style.opacity === '0' || parseFloat(style.opacity) < 1) {
          el.style.opacity = '1';
          el.style.transform = 'none';
          el.style.animation = 'none';
          triggerCountUpInBlock(el);
        }
        return;
      }
      if (isRevealOnLoad(el)) {
        if (!el.classList.contains('is-visible')) revealBlock(el);
        return;
      }
      if (!el.classList.contains('is-visible')) {
        revealBlock(el);
      }
    }, SAFETY_NET_MS);
  });
}

function initRevealBlocks() {
  const blocks = document.querySelectorAll(BLOCK_SELECTOR);
  if (!blocks.length) return;

  if (prefersReducedMotion()) {
    blocks.forEach((el) => {
      el.classList.add('is-visible');
      if (isRevealLoad(el)) {
        el.style.opacity = '1';
        el.style.transform = 'none';
        el.style.animation = 'none';
      }
      if (isRevealOnLoad(el)) {
        el.classList.add('is-visible');
      }
      triggerCountUpInBlock(el);
    });
    return;
  }

  blocks.forEach(syncLoadRevealDelay);

  /** @type {Element[]} */
  const observeList = [];

  blocks.forEach((el) => {
    if (isRevealLoad(el)) {
      const delay = parseInt(el.getAttribute('data-n50-delay') || '0', 10) || 0;
      setTimeout(() => triggerCountUpInBlock(el), blockZoneDuration(el) + delay);
      return;
    }
    if (isRevealOnLoad(el)) {
      revealBlock(el);
      return;
    }
    observeList.push(el);
  });

  revealBlocksOnLoad(blocks);

  const remaining = observeList.filter((el) => !el.classList.contains('is-visible'));

  if (remaining.length) {
    observeWithModes(remaining, revealBlock);
  }

  scheduleBlockSafetyNet(blocks);
}

function initReveals() {
  const elements = document.querySelectorAll(REVEAL_SELECTOR);
  if (!elements.length) return;

  if (prefersReducedMotion()) {
    elements.forEach((el) => el.classList.add('is-visible'));
    document.querySelectorAll('[data-n50-reveal-load]').forEach((el) => {
      el.style.opacity = '1';
      el.style.transform = 'none';
      el.style.animation = 'none';
    });
    return;
  }

  /** @type {Element[]} */
  const observeList = [];

  elements.forEach((el) => {
    if (el.hasAttribute('data-n50-reveal-load')) return;
    observeList.push(el);
  });

  if (!observeList.length) return;

  observeWithModes(observeList, revealElement);
}

function initCountUp() {
  const counters = document.querySelectorAll(COUNT_SELECTOR);
  if (!counters.length) return;

  if (prefersReducedMotion()) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = /** @type {HTMLElement} */ (entry.target);
        if (el.dataset.n50CountDone === 'true') return;

        const text = el.textContent?.trim() || '';
        if (!isCountUpEligible(text)) {
          el.removeAttribute('data-n50-count-up');
          observer.unobserve(el);
          return;
        }

        el.dataset.n50CountDone = 'true';
        animateCountUp(el);
        observer.unobserve(el);
      });
    },
    { rootMargin: SCROLL_ROOT_MARGIN, threshold: SCROLL_THRESHOLD }
  );

  counters.forEach((el) => {
    if (el.closest(BLOCK_SELECTOR)) return;

    const text = el.textContent?.trim() || '';
    if (!isCountUpEligible(text)) {
      el.removeAttribute('data-n50-count-up');
      return;
    }
    observer.observe(el);
  });
}

function initBenefitsImageCrossfade() {
  const accordion = document.querySelector('[data-benefits-accordion]');
  const panelImg = document.querySelector('[data-benefits-panel-image]');
  if (!accordion || !panelImg || !(panelImg instanceof HTMLImageElement)) return;
  if (prefersReducedMotion()) return;

  accordion.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    const nextSrc = details.getAttribute('data-benefit-image');
    if (!nextSrc || panelImg.src === nextSrc) return;

    panelImg.style.opacity = '0';
    const onLoad = () => {
      panelImg.style.opacity = '1';
      panelImg.removeEventListener('load', onLoad);
    };
    panelImg.addEventListener('load', onLoad);
    panelImg.src = nextSrc;
  });
}

function initHeaderScroll() {
  const header = document.getElementById('header-component');
  if (!header || prefersReducedMotion()) return;

  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      header.classList.toggle('n50-header--scrolled', window.scrollY > 80);
      ticking = false;
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

function initAll() {
  applyStaggerDelays();
  initRevealBlocks();
  initReveals();
  initCountUp();
}

/** Must stay just under the CSS failsafe delay (n50-reveal-failsafe in next50-brand.liquid, 3s). */
const FAILSAFE_HANDOFF_MS = 2800;

/**
 * The CSS failsafe reveals everything after 3s unless <html> has .n50-reveal-ready.
 * Add the class only once init has succeeded; if we start after the failsafe window, keep content shown.
 */
function handOffFromCssFailsafe() {
  if (performance.now() > FAILSAFE_HANDOFF_MS) {
    document.querySelectorAll(`${REVEAL_SELECTOR}, ${BLOCK_SELECTOR}`).forEach((el) => el.classList.add('is-visible'));
  }
  document.documentElement.classList.add('n50-reveal-ready');
}

onDocumentReady(() => {
  initAll();
  handOffFromCssFailsafe();
  initBenefitsImageCrossfade();
  initHeaderScroll();

  document.addEventListener('shopify:section:load', initAll);
});
